import * as React from "react";
import {
    Badge,
    Box,
    Button,
    Container,
    Flex,
    Grid,
    Heading,
    HStack,
    Link,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Stack,
    Text,
    useToast
} from "@chakra-ui/react";
import {useEffect, useMemo, useState} from "react";
import {useQuery} from "@apollo/client";
import {Link as RouterLink} from "react-router-dom";
import QRCode from "react-qr-code";
import {AdvancedMarker, APIProvider, Map, useMap} from "@vis.gl/react-google-maps";
import {getPublicAreasQuery} from "../components/area/areaQueries";
import {BiCopy, BiLockAlt, BiQrScan} from "react-icons/bi";
import {FaGooglePlay, FaApple} from "react-icons/fa";

const GOOGLE_MAPS_API_KEY = "AIzaSyCcdolz5IqPuQZ5MuZdXEHVgdoTukvbblE";
const PUBLIC_AREAS_MAP_ID = "SOUNDSPOTS_PUBLIC_AREAS";

const T = {
    bg: "#FDFBF7",
    bgCard: "#FAF6EE",
    bgCardHi: "#F2EDE3",
    bgDeep: "#EDE5D6",
    border: "#E0D4BC",
    borderHi: "#C8B090",
    primary: "#8A4E16",
    primaryBright: "#A0622A",
    primaryLight: "#C4721A",
    ink: "#1E160A",
    inkMid: "#5A3E22",
    inkDim: "#A08060",
    inkFaint: "#C8B090"
};

type PublicArea = {
    id: string
    releasedName: string
    organizationId: string
    organizationName: string
    summary?: string
    description?: string
    creator?: string
    coverImageUrl?: string
    position?: {lat: number; lng: number}
    availableLanguageCodes: string[]
    publishedAt: number
    hasAccessCode: boolean
    downloadFrom?: string
    downloadTo?: string
    playFrom?: string
    playTo?: string
}

const formatTimingForDisplay = (iso: string | undefined): string | undefined => {
    if (!iso) {
        return undefined;
    }
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/);
    if (!match) {
        return iso;
    }
    const [, year, month, day, time, rawOffset] = match;
    const offset = rawOffset === "Z" ? "+00:00" : (rawOffset || "");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthLabel = monthNames[Number.parseInt(month, 10) - 1] || month;
    return `${monthLabel} ${Number.parseInt(day, 10)}, ${year} ${time}${offset ? ` (${offset})` : ""}`;
};

const formatPlayWindow = (area: PublicArea): string | undefined => {
    if (!area.playFrom && !area.playTo) {
        return undefined;
    }
    if (area.playFrom === area.downloadFrom && area.playTo === area.downloadTo) {
        return undefined;
    }
    const from = formatTimingForDisplay(area.playFrom);
    const to = formatTimingForDisplay(area.playTo);
    if (from && to) {
        return `${from} → ${to}`;
    }
    if (from) {
        return `From ${from}`;
    }
    if (to) {
        return `Until ${to}`;
    }
    return undefined;
};

type PublicAreaQrButtonProps = {
    area: PublicArea
}

const ArcMark = ({size = 28, color = T.primary}) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <circle cx="24" cy="34" r="2.8" fill={color}/>
        <path d="M15 35.94 A9 9 0 0 1 33 35.94" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round"/>
        <path d="M10 34.85 A14 14 0 0 1 38 34.85" stroke={color} strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.55"/>
        <path d="M5 33.30 A19 19 0 0 1 43 33.30" stroke={color} strokeWidth="1.0" fill="none" strokeLinecap="round" opacity="0.3"/>
        <path d="M5 35.5 Q24 42.3 43 35.5" stroke={color} strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.45"/>
    </svg>
);

const PublicAreaQrButton = ({area}: PublicAreaQrButtonProps) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const qrValue = useMemo(() => JSON.stringify({
        a: area.id,
        c: area.organizationId,
        n: area.releasedName
    }), [area.id, area.organizationId, area.releasedName]);

    return (
        <>
            <Button
                leftIcon={<BiQrScan/>}
                onClick={() => setOpen(true)}
                bg={T.primary}
                color={T.bg}
                borderRadius={"10px"}
                fontSize={"12px"}
                fontWeight={700}
                letterSpacing={"0.06em"}
                textTransform={"uppercase"}
                _hover={{bg: T.primaryBright}}
            >
                Show QR Code
            </Button>
            <Modal isOpen={isOpen} onClose={() => setOpen(false)} isCentered>
                <ModalOverlay bg={"rgba(14,10,4,0.55)"}/>
                <ModalContent
                    maxW={"320px"}
                    bg={T.bg}
                    borderRadius={"20px"}
                    border={`1px solid ${T.borderHi}`}
                    boxShadow={"0 24px 64px rgba(14,10,4,0.28)"}
                    overflow={"hidden"}
                >
                    <ModalHeader pt={8} pb={0}>
                        <Stack spacing={1} textAlign={"center"}>
                            <Text fontSize={"11px"} color={T.inkDim} letterSpacing={"0.14em"} textTransform={"uppercase"}>
                                {area.organizationName}
                            </Text>
                            <Text fontSize={"20px"} color={T.ink} fontFamily={"Georgia, serif"} letterSpacing={"-0.01em"}>
                                {area.releasedName}
                            </Text>
                        </Stack>
                    </ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody py={8}>
                        <Stack spacing={5} align={"center"}>
                            <Text color={T.inkMid} textAlign={"center"} fontSize={"13px"}>
                                Scan the QR code in the SoundSpots app.
                            </Text>
                            <Box
                                width={"180px"}
                                height={"180px"}
                                bg={T.bgCard}
                                borderRadius={"12px"}
                                border={`1px solid ${T.border}`}
                                display={"flex"}
                                alignItems={"center"}
                                justifyContent={"center"}
                                p={3}
                            >
                                <QRCode value={qrValue} size={144}/>
                            </Box>

                            {area.hasAccessCode ? (
                                <HStack
                                    spacing={2}
                                    width={"100%"}
                                    bg={T.bgCard}
                                    borderRadius={"10px"}
                                    border={`1px solid ${T.border}`}
                                    px={4}
                                    py={3}
                                    align={"flex-start"}
                                >
                                    <BiLockAlt color={T.inkDim} size={14}/>
                                    <Text fontSize={"11px"} color={T.inkMid} lineHeight={1.5}>
                                        An access code will be required in the app after scanning.
                                    </Text>
                                </HStack>
                            ) : null}

                            <Button
                                variant={"outline"}
                                onClick={() => setOpen(false)}
                                borderColor={T.border}
                                color={T.inkMid}
                                borderRadius={"10px"}
                                fontSize={"12px"}
                                fontWeight={600}
                                letterSpacing={"0.04em"}
                                px={6}
                            >
                                Close
                            </Button>
                        </Stack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

const PublicAreaCopyLinkButton = ({area}: {area: PublicArea}) => {
    const toast = useToast();
    const url = useMemo(() => (
        `https://soundspots.app/load`
        + `?c=${encodeURIComponent(area.organizationId)}`
        + `&a=${encodeURIComponent(area.id)}`
        + `&n=${encodeURIComponent(area.releasedName)}`
    ), [area.id, area.organizationId, area.releasedName]);

    const handleCopy = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const input = document.createElement("input");
                input.value = url;
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                document.body.removeChild(input);
            }
            toast({
                title: "Link copied",
                description: `Copied public link for ${area.releasedName}.`,
                status: "success",
                isClosable: true
            });
        } catch (error) {
            toast({
                title: "Couldn't copy link",
                description: error instanceof Error ? error.message : "Clipboard access was denied.",
                status: "error",
                isClosable: true
            });
        }
    };

    return (
        <Button
            leftIcon={<BiCopy/>}
            onClick={handleCopy}
            variant={"outline"}
            borderColor={T.primary}
            color={T.primary}
            borderRadius={"10px"}
            fontSize={"12px"}
            fontWeight={700}
            letterSpacing={"0.06em"}
            textTransform={"uppercase"}
            _hover={{bg: `${T.primary}10`}}
        >
            Copy Link
        </Button>
    );
};

const PlayStoreBadge = () => (
    <Link
        href={"https://play.google.com/store/apps/details?id=com.soundspots"}
        isExternal
        _hover={{textDecoration: "none", opacity: 0.88}}
        display={"flex"}
        alignItems={"center"}
        gap={3}
        bg={T.ink}
        color={"white"}
        px={5}
        py={3}
        borderRadius={"12px"}
        width={"100%"}
    >
        <Box as={FaGooglePlay} boxSize={5}/>
        <Box>
            <Text fontSize={"9px"} color={"rgba(255,255,255,0.6)"} letterSpacing={"0.12em"} textTransform={"uppercase"} mb={0.5}>
                Get it on
            </Text>
            <Text fontSize={"15px"} fontWeight={700} letterSpacing={"0.01em"}>
                Google Play
            </Text>
        </Box>
    </Link>
);

const AppStoreBadge = () => (
    <Link
        href={"https://apps.apple.com/app/id6766112877"}
        isExternal
        _hover={{textDecoration: "none", opacity: 0.88}}
        display={"flex"}
        alignItems={"center"}
        gap={3}
        bg={T.ink}
        color={"white"}
        px={5}
        py={3}
        borderRadius={"12px"}
        width={"100%"}
    >
        <Box as={FaApple} boxSize={5}/>
        <Box>
            <Text fontSize={"9px"} color={"rgba(255,255,255,0.6)"} letterSpacing={"0.12em"} textTransform={"uppercase"} mb={0.5}>
                Download on the
            </Text>
            <Text fontSize={"15px"} fontWeight={700} letterSpacing={"0.01em"}>
                App Store
            </Text>
        </Box>
    </Link>
);

const PublicAreaCard = ({area}: {area: PublicArea}) => {
    const description = area.description?.trim()
        || area.summary?.trim()
        || "";
    const playWindow = formatPlayWindow(area);

    return (
        <Box
            id={`public-area-${area.id}`}
            scrollMarginTop={{base: "72px", xl: "440px"}}
            bg={T.bg}
            borderRadius={"16px"}
            border={`1px solid ${T.border}`}
            boxShadow={"0 2px 12px rgba(30,22,10,0.06)"}
            overflow={"hidden"}
            transition={"box-shadow 0.15s ease, border-color 0.15s ease"}
            _hover={{
                borderColor: T.borderHi,
                boxShadow: "0 4px 20px rgba(30,22,10,0.10)"
            }}
        >
            {area.coverImageUrl ? (
                <Image
                    src={area.coverImageUrl}
                    alt={`${area.releasedName} cover`}
                    width={"100%"}
                    maxH={"160px"}
                    objectFit={"cover"}
                    borderTopRadius={"16px"}
                />
            ) : null}
            <Box px={{base: 5, md: 6}} py={4} borderBottom={`1px solid ${T.border}`}>
                <HStack align={"flex-start"} justify={"space-between"} spacing={3}>
                    <Box flex={1}>
                        <Text
                            fontSize={"10px"}
                            color={T.inkDim}
                            letterSpacing={"0.12em"}
                            textTransform={"uppercase"}
                            fontWeight={600}
                            mb={1.5}
                        >
                            {area.organizationName}
                        </Text>
                        <Heading
                            size={"md"}
                            color={T.ink}
                            fontFamily={"Georgia, serif"}
                            letterSpacing={"-0.01em"}
                            lineHeight={1.2}
                        >
                            {area.releasedName}
                        </Heading>
                    </Box>

                    {area.hasAccessCode ? (
                        <HStack
                            spacing={1.5}
                            bg={T.bgCard}
                            border={`1px solid ${T.border}`}
                            borderRadius={"8px"}
                            px={2.5}
                            py={1.5}
                            flexShrink={0}
                            mt={0.5}
                        >
                            <BiLockAlt color={T.inkDim} size={12}/>
                            <Text
                                fontSize={"9px"}
                                color={T.inkDim}
                                fontWeight={700}
                                letterSpacing={"0.10em"}
                                textTransform={"uppercase"}
                            >
                                Code required
                            </Text>
                        </HStack>
                    ) : null}
                </HStack>
            </Box>

            <Box px={{base: 5, md: 6}} py={4}>
                <Text fontSize={"13px"} color={T.inkMid} lineHeight={1.6} mb={3} noOfLines={2}>
                    {description}
                </Text>

                {playWindow ? (
                    <Box
                        bg={T.bgCard}
                        border={`1px solid ${T.border}`}
                        borderRadius={"8px"}
                        px={3}
                        py={2}
                        mb={3}
                    >
                        <Text
                            fontSize={"9px"}
                            color={T.inkDim}
                            fontWeight={700}
                            letterSpacing={"0.10em"}
                            textTransform={"uppercase"}
                            mb={0.5}
                        >
                            Playable
                        </Text>
                        <Text fontSize={"12px"} color={T.inkMid}>
                            {playWindow}
                        </Text>
                    </Box>
                ) : null}

                {(() => {
                    const metaItems: {key: string; node: React.ReactNode}[] = [];
                    if (area.creator) {
                        metaItems.push({
                            key: "creator",
                            node: (
                                <Text fontSize={"11px"} color={T.inkFaint}>
                                    By {area.creator}
                                </Text>
                            )
                        });
                    }
                    if (area.hasAccessCode) {
                        metaItems.push({
                            key: "access",
                            node: (
                                <Text fontSize={"11px"} color={T.inkFaint}>
                                    Access code requested in app
                                </Text>
                            )
                        });
                    }
                    if (area.availableLanguageCodes.length > 0) {
                        metaItems.push({
                            key: "languages",
                            node: (
                                <HStack spacing={1} flexWrap={"wrap"}>
                                    {area.availableLanguageCodes.map((languageCode) => (
                                        <Text
                                            key={`${area.id}-${languageCode}`}
                                            fontSize={"9px"}
                                            color={T.inkMid}
                                            bg={T.bgCard}
                                            border={`1px solid ${T.border}`}
                                            borderRadius={"4px"}
                                            px={1.5}
                                            py={0.5}
                                            fontWeight={700}
                                            letterSpacing={"0.06em"}
                                        >
                                            {languageCode}
                                        </Text>
                                    ))}
                                </HStack>
                            )
                        });
                    }
                    if (metaItems.length === 0) {
                        return null;
                    }
                    return (
                        <HStack spacing={3} flexWrap={"wrap"} mb={5}>
                            {metaItems.map((item, index) => (
                                <React.Fragment key={item.key}>
                                    {index > 0 ? (
                                        <Box width={"3px"} height={"3px"} borderRadius={"full"} bg={T.inkFaint}/>
                                    ) : null}
                                    {item.node}
                                </React.Fragment>
                            ))}
                        </HStack>
                    );
                })()}

                <HStack spacing={2} flexWrap={"wrap"}>
                    <PublicAreaQrButton area={area}/>
                    <PublicAreaCopyLinkButton area={area}/>
                </HStack>
            </Box>
        </Box>
    );
};

type PublicAreasMapProps = {
    areas: PublicArea[]
    onMarkerClick: (id: string) => void
}

const PublicAreasMapFitter = ({positioned}: {positioned: PublicArea[]}) => {
    const map = useMap();
    useEffect(() => {
        if (!map || positioned.length === 0 || typeof google === "undefined") {
            return;
        }
        if (positioned.length === 1) {
            map.setCenter(positioned[0].position!);
            map.setZoom(13);
            return;
        }
        const bounds = new google.maps.LatLngBounds();
        positioned.forEach((area) => bounds.extend(area.position!));
        map.fitBounds(bounds, 64);
    }, [map, positioned]);
    return null;
};

const PublicAreasMap = ({areas, onMarkerClick}: PublicAreasMapProps) => {
    const positioned = useMemo(() => areas.filter((area) =>
        area.position && Number.isFinite(area.position.lat) && Number.isFinite(area.position.lng)
    ), [areas]);

    if (positioned.length === 0) {
        return null;
    }

    const initialCenter = positioned[0].position!;

    return (
        <Box
            position={{base: "static", xl: "sticky"}}
            top={{xl: "72px"}}
            zIndex={{xl: 10}}
            height={{base: "260px", md: "340px"}}
            mb={9}
            borderRadius={"16px"}
            overflow={"hidden"}
            border={`1px solid ${T.border}`}
            boxShadow={"0 2px 12px rgba(30,22,10,0.06)"}
            bg={T.bg}
        >
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <Map
                    mapId={PUBLIC_AREAS_MAP_ID}
                    defaultCenter={initialCenter}
                    defaultZoom={positioned.length === 1 ? 13 : 4}
                    style={{width: "100%", height: "100%"}}
                    gestureHandling={"greedy"}
                    streetViewControl={false}
                    fullscreenControl={false}
                >
                    <PublicAreasMapFitter positioned={positioned}/>
                    {positioned.map((area) => (
                        <AdvancedMarker
                            key={area.id}
                            position={area.position!}
                            title={area.releasedName}
                            onClick={() => onMarkerClick(area.id)}
                        />
                    ))}
                </Map>
            </APIProvider>
        </Box>
    );
};

const PublicAreaListPage: React.FC = () => {
    const {loading, data} = useQuery(getPublicAreasQuery);
    const publicAreas: PublicArea[] = data?.getPublicAreas || [];

    const handleMarkerClick = (areaId: string) => {
        const target = document.getElementById(`public-area-${areaId}`);
        if (target) {
            target.scrollIntoView({behavior: "smooth", block: "start"});
        }
    };

    return (
        <Box
            height={"100vh"}
            overflowY={"auto"}
            bg={T.bg}
            color={T.ink}
            fontFamily={"system-ui, sans-serif"}
        >
            <Box
                as={"nav"}
                position={"sticky"}
                top={0}
                zIndex={20}
                bg={T.bg}
                borderBottom={`1px solid ${T.border}`}
            >
                <Container maxW={"7xl"} px={{base: 5, md: 8}}>
                    <Flex height={"56px"} align={"center"} justify={"space-between"}>
                        <HStack spacing={2.5}>
                            <ArcMark size={24}/>
                            <Text fontSize={"15px"} color={T.ink} fontFamily={"Georgia, serif"} letterSpacing={"0.01em"}>
                                soundspots
                            </Text>
                            <Text fontSize={"10px"} color={T.inkFaint} letterSpacing={"0.06em"}>
                                .app
                            </Text>
                        </HStack>
                        <Link as={RouterLink} to={"/"} fontSize={"12px"} color={T.inkMid} fontWeight={600} letterSpacing={"0.04em"}>
                            Go to Administration
                        </Link>
                    </Flex>
                </Container>
            </Box>

            <Container
                maxW={"7xl"}
                px={{base: 5, md: 8}}
                py={{base: 10, md: 12}}
            >
                <Grid
                    templateColumns={{base: "1fr", xl: "minmax(0, 1fr) 300px"}}
                    gap={{base: 10, xl: 10}}
                    alignItems={"start"}
                >
                    <Box>
                        <Box mb={9} flexShrink={0}>
                            <Badge
                                display={"inline-block"}
                                fontSize={"10px"}
                                fontWeight={700}
                                letterSpacing={"0.16em"}
                                textTransform={"uppercase"}
                                color={T.primary}
                                bg={`${T.primary}12`}
                                border={`1px solid ${T.primary}28`}
                                borderRadius={"6px"}
                                px={2.5}
                                py={1}
                                mb={4}
                            >
                                Public Spaces
                            </Badge>
                            <Heading
                                fontSize={{base: "30px", md: "36px"}}
                                color={T.ink}
                                fontFamily={"Georgia, serif"}
                                letterSpacing={"-0.02em"}
                                lineHeight={1.15}
                                maxW={"520px"}
                                mb={4}
                            >
                                Explore SoundSpots experiences open to everyone.
                            </Heading>
                            <Text fontSize={"14px"} color={T.inkMid} lineHeight={1.7} maxW={"540px"}>
                                Browse publicly available spaces and scan a QR code with the SoundSpots app on site. <br/>
                                A lock icon means the app will ask for an access code after scanning.
                            </Text>
                        </Box>

                        <PublicAreasMap areas={publicAreas} onMarkerClick={handleMarkerClick}/>

                        {loading ? (
                            <Flex justify={"center"} py={20}>
                                <Spinner size={"xl"} thickness={"3px"} color={T.primary}/>
                            </Flex>
                        ) : publicAreas.length === 0 ? (
                            <Box
                                bg={T.bgCard}
                                borderRadius={"16px"}
                                border={`1px solid ${T.border}`}
                                p={{base: 6, md: 8}}
                            >
                                <Stack spacing={3} align={"flex-start"}>
                                    <Heading size={"md"} fontFamily={"Georgia, serif"} color={T.ink}>
                                        No public spaces yet
                                    </Heading>
                                    <Text color={T.inkMid} lineHeight={1.7}>
                                        Public experiences will appear here once they have been published and opened to the audience.
                                    </Text>
                                </Stack>
                            </Box>
                        ) : (
                            <Stack spacing={7}>
                                {publicAreas.map((area) => (
                                    <PublicAreaCard key={area.id} area={area}/>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Box position={{base: "static", xl: "sticky"}} top={"72px"}>
                        <Stack spacing={3}>
                            <Box
                                bg={T.bgCard}
                                borderRadius={"16px"}
                                border={`1px solid ${T.border}`}
                                px={6}
                                py={6}
                            >
                                <Text
                                    fontSize={"9px"}
                                    fontWeight={700}
                                    letterSpacing={"0.18em"}
                                    textTransform={"uppercase"}
                                    color={T.inkDim}
                                    mb={4}
                                >
                                    Get the app
                                </Text>

                                <Stack spacing={2}>
                                    <PlayStoreBadge/>
                                    <AppStoreBadge/>
                                </Stack>

                                <Text fontSize={"11px"} color={T.inkFaint} lineHeight={1.6} mt={3}>
                                    Scan any QR code below with the SoundSpots app to load a space on your device.
                                </Text>
                            </Box>

                            <Flex justify={"center"} pt={2} opacity={0.35}>
                                <ArcMark size={32} color={T.inkDim}/>
                            </Flex>
                        </Stack>
                    </Box>
                </Grid>
            </Container>
        </Box>
    );
};

export default PublicAreaListPage;
